import { formatInTimeZone } from "date-fns-tz";

import { getServiceIcon } from "Components/ServiceIcon/helper";
import { roundedNumber, sortAlphabetically } from "Libs/utils";

import type {
  AddClassProps,
  DeprecatedImageEntry,
  ReturnAddClass,
  ResourceCollectionType,
  TotalResources
} from "./types";
import type {
  Deployment,
  DeploymentService,
  DeploymentServices
} from "Api/organizations/projects/environments/deployments/types";

export const addClass = (
  list: AddClassProps["list"] = {},
  newClass: AddClassProps["newClass"]
) => {
  return Object.keys(list).reduce<ReturnAddClass>((current, key) => {
    const icon = getServiceIcon(list[key]).iconName;
    const name = list[key]?.name || key;

    current[key] = { icon, class: newClass, ...list[key], name };
    return current;
  }, {});
};

export const addClassWorker = (
  list: [string, DeploymentService][] = [],
  newClass: AddClassProps["newClass"]
) => {
  return list.reduce<ReturnAddClass>((acc, [key, value], index) => {
    const icon = getServiceIcon(value).iconName;
    const name = value?.name || key;

    acc[index] = { icon, class: newClass, ...value, name };

    return acc;
  }, {});
};

export const retrieveOneResourceRawDataHandler = ({
  currentDeployment,
  resourceType,
  appName
}: {
  currentDeployment: Deployment | undefined;
  resourceType: ResourceCollectionType;
  appName: string;
}) => {
  const allProfiles = currentDeployment?.container_profiles;
  const resource = currentDeployment?.[resourceType];
  const resourceApp = resource?.[appName];
  const cronsQuantity = Object.keys(resourceApp?.crons ?? {}).length;
  const profileSize = resourceApp?.resources?.profile_size;
  const containerProfile = resourceApp?.container_profile;
  const currentProfile = allProfiles?.[containerProfile!];
  const currentCpu = currentProfile?.[profileSize!]?.cpu ?? 0;
  const cpu = {
    shared: profileSize?.endsWith(".gc") ? 0 : currentCpu,
    guaranteed: profileSize?.endsWith(".gc") ? currentCpu : 0,
    total: currentCpu
  };
  const ram = currentProfile?.[profileSize!].memory ?? 0;
  const disk = resourceApp?.disk ?? 0;
  const instances = resourceApp?.instance_count ?? 1;
  const status = currentDeployment?.environment_info?.status ?? "";

  return { cpu, ram, instances, disk, status, cronsQuantity, containerProfile };
};

export const resourcesAccumulatorHandler = (
  totalResources: TotalResources,
  resourceItem: { [K in keyof TotalResources]?: TotalResources[K] | null }
): TotalResources & { status: string } => {
  const updatedCpu = {
    shared: resourceItem?.cpu?.shared ?? 0,
    guaranteed: resourceItem?.cpu?.guaranteed ?? 0,
    total: resourceItem?.cpu?.total ?? 0
  };
  const ram = resourceItem?.ram ?? 0;
  const instances = resourceItem?.instances ?? 1;
  const disk = resourceItem?.disk ?? 0;
  const object = resourceItem?.object ?? 0;
  const status = resourceItem?.status ?? "";

  updatedCpu.shared = totalResources.cpu.shared + updatedCpu.shared * instances;
  updatedCpu.guaranteed =
    totalResources.cpu.guaranteed + updatedCpu.guaranteed * instances;
  updatedCpu.total = updatedCpu.shared + updatedCpu.guaranteed;

  return {
    cpu: updatedCpu,
    ram: totalResources.ram + ram * instances,
    instances: totalResources.instances + instances,
    disk: totalResources.disk + disk,
    object: (totalResources.object ?? 0) + object,
    status
  };
};

export const resourcesFormatHandler = (
  totalResources: TotalResources
): TotalResources => ({
  cpu: {
    shared: roundedNumber(totalResources.cpu.shared, 3),
    guaranteed: roundedNumber(totalResources.cpu.guaranteed, 3),
    total: roundedNumber(totalResources.cpu.total, 3)
  },
  ram: roundedNumber(totalResources.ram / 1024, 3),
  instances: totalResources.instances,
  disk: roundedNumber(totalResources.disk / 1024, 3),
  // Always a number: the accumulator normalises a missing allocation to 0, so
  // a falsy total only ever means "zero", never "unknown". Reporting it as
  // undefined made consumers unable to tell 0 TB from no data (CON-1699).
  object: roundedNumber((totalResources.object ?? 0) / 1024 / 1024, 3),
  status: totalResources.status
});

export const deploymentResourcesHandler = (deployment?: Deployment) => {
  let totalResources: TotalResources & { status: string } = {
    cpu: {
      shared: 0,
      guaranteed: 0,
      total: 0
    },
    ram: 0,
    instances: 0,
    disk: 0,
    object: 0,
    status: ""
  };
  const { webapps, workers, services } = deployment ?? {
    webapps: undefined,
    workers: undefined,
    services: undefined
  };
  const status = deployment?.environment_info?.status ?? "";

  try {
    Object.values({ webapps, workers, services }).forEach(
      resourceCollection => {
        Object.values(resourceCollection ?? {}).forEach(data => {
          const containerProfiles =
            deployment?.container_profiles[data.container_profile ?? ""];

          const profileSize = data?.resources.profile_size ?? "";

          const currentCpu = containerProfiles?.[profileSize].cpu ?? 0;

          const cpu = {
            shared: profileSize?.endsWith(".gc") ? 0 : currentCpu,
            guaranteed: profileSize?.endsWith(".gc") ? currentCpu : 0,
            total: currentCpu
          };

          const ram = containerProfiles?.[profileSize].memory;

          const { disk, instance_count: instances } = data;
          const object = data.resources?.disk?.object ?? 0;

          totalResources = resourcesAccumulatorHandler(totalResources, {
            cpu,
            ram,
            instances,
            disk,
            object,
            status
          });
        });
      }
    );

    return resourcesFormatHandler(totalResources);
  } catch {
    return {
      cpu: {
        shared: 0,
        guaranteed: 0,
        total: 0
      },
      ram: 0,
      instances: 0,
      disk: 0,
      object: 0,
      status
    };
  }
};

export const transformServices = (
  services: DeploymentServices,
  kind: "webapp" | "service" | "worker"
) =>
  sortAlphabetically(
    Object.entries(services).map(([key, value]) => ({
      ...value,
      icon: value.type.split(":")[0],
      name: key,
      kind
    })),
    service => service.name
  );

export const checkNullProfileSize = ({
  nextValues
}: {
  nextValues:
    | Record<
        string,
        Record<string, { resources?: { profile_size?: string | null } }>
      >
    | undefined;
}) => {
  const resourceTypes = ["services", "workers", "webapps"];

  for (const resourceType of resourceTypes) {
    const resources = nextValues?.[resourceType];
    if (resources) {
      for (const resourceName in resources) {
        const profileSize = resources[resourceName]?.resources?.profile_size;
        if (profileSize === null || profileSize === undefined) {
          return true;
        }
      }
    }
  }

  return false;
};

/** Parse date-only strings (e.g. "2026-12-02") as UTC midnight */
export const parseUTCDate = (dateStr: string) =>
  new Date(dateStr + "T00:00:00Z");

export const SUNSET_PRE_WARNING_DAYS = 30;

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Derive formatted dates and daysRemaining from an upstream version entry */
export const formatUpstreamImageDates = (
  upstreamVersion: DeprecatedImageEntry["upstreamVersion"] | undefined
) => {
  const sunsetDate = upstreamVersion?.sunset_at
    ? formatInTimeZone(
        parseUTCDate(upstreamVersion.sunset_at),
        "UTC",
        "MMM dd, yyyy"
      )
    : undefined;

  const decommissionDate = upstreamVersion?.decommissioned_at
    ? formatInTimeZone(
        parseUTCDate(upstreamVersion.decommissioned_at),
        "UTC",
        "MMM dd, yyyy"
      )
    : undefined;

  const daysRemaining = upstreamVersion?.decommissioned_at
    ? (parseUTCDate(upstreamVersion.decommissioned_at).getTime() -
        parseUTCDate(
          formatInTimeZone(new Date(), "UTC", "yyyy-MM-dd")
        ).getTime()) /
      MS_PER_DAY
    : undefined;

  return { sunsetDate, decommissionDate, daysRemaining };
};

/**
 * Returns true if sunsetAt is within the pre-warning window or already past.
 * Used by the selector to include "active" images approaching/past sunset.
 * The hook's isWithinPreWarningPeriod further narrows this to only
 * future dates within the window (excluding already-past-sunset images).
 */
export const hasSunsetWarningTriggered = (sunsetAt: Date) => {
  const daysUntilSunset = Math.ceil(
    (sunsetAt.getTime() - Date.now()) / MS_PER_DAY
  );
  return daysUntilSunset <= SUNSET_PRE_WARNING_DAYS;
};

/**
 * Extract the clean image id (e.g. "php:8.4") from a node type string that
 * may include a process-manager suffix and build number ("php:8.4-systemd:197").
 */
export const extractImageId = (type: string | undefined) => {
  if (!type) return undefined;
  const [name, versionSegment] = type.split(":");
  if (!versionSegment) return type;
  // Strip process-manager suffix like "-systemd" from the version segment
  const version = versionSegment.replace(/-\w+$/, "");
  return `${name}:${version}`;
};

export const checkImageStatus = ({
  decommissionedImages,
  sunsetImages,
  node
}: {
  decommissionedImages?: DeprecatedImageEntry[];
  sunsetImages?: DeprecatedImageEntry[];
  node: { type: string };
}) => {
  const nodeType = extractImageId(node.type);
  return {
    isDecommissioned:
      decommissionedImages?.some(di => nodeType === di.id) ?? false,
    isSunset: sunsetImages?.some(si => nodeType === si.id) ?? false
  };
};

const defaultImageStatus = { isSunset: false, isDecommissioned: false };

export const getServiceImageStatus = (
  imageStatusMap: Record<
    string,
    { isSunset: boolean; isDecommissioned: boolean }
  >,
  serviceType: string | undefined
) => {
  const key = extractImageId(serviceType);
  return (key && imageStatusMap[key]) || defaultImageStatus;
};
